import {useCallback, useState} from 'react';
import {ActivityIndicator, Modal, ScrollView, StyleSheet, Text, TouchableOpacity, View} from 'react-native';
import {useTranslation} from 'react-i18next';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import Markdown from 'react-native-markdown-display';
import {Square, SquareCheck} from 'lucide-react-native';
import {useCurrentTheme} from '@/theming/useCurrentTheme';
import {ThemeType} from '@/theming/baseTheme';
import {useCodeOfConductPending, useInvalidator} from '@/data/app-data-hooks';
import {useAcceptCodeOfConduct, useCodeOfConductStatus} from '@/data/code-of-conduct-hooks';
import {isCodeOfConductOutdatedError} from '@/data/code-of-conduct-api';
import {useHardwareBack} from '@/hooks/useHardwareBack';

/**
 * Org-scoped blocking gate: while /app-data reports codeOfConductPending for this organization,
 * a non-dismissible modal shows the current code of conduct version (localized Markdown) and
 * blocks the org UI until the user accepts it. The gate clears once /app-data is refreshed.
 */
export const CodeOfConductGate = ({orgId}: {orgId: string}) => {
    const {data: pending} = useCodeOfConductPending(orgId);

    if (!pending) {
        return null;
    }

    return <CodeOfConductModal orgId={orgId}/>;
};

const CodeOfConductModal = ({orgId}: {orgId: string}) => {
    const {t} = useTranslation();
    const {theme} = useCurrentTheme();
    const insets = useSafeAreaInsets();
    const styles = createStyles(theme, insets);
    const {invalidateAppData} = useInvalidator();

    const {data: status, isLoading, isError, refetch, isRefetching} = useCodeOfConductStatus(orgId);
    const acceptMutation = useAcceptCodeOfConduct(orgId);

    const [accepted, setAccepted] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [newVersionNotice, setNewVersionNotice] = useState(false);
    const [finishing, setFinishing] = useState(false);

    useHardwareBack(useCallback(() => true, []));

    const version = status?.version ?? null;
    const busy = acceptMutation.isPending || finishing;

    const handleAccept = async () => {
        if (!version || !accepted) {
            return;
        }
        setError(null);
        setNewVersionNotice(false);
        try {
            await acceptMutation.mutateAsync({versionId: version.versionId, locale: version.locale});
            setFinishing(true);
            try {
                await invalidateAppData();
            } finally {
                setFinishing(false);
            }
        } catch (e) {
            if (isCodeOfConductOutdatedError(e)) {
                setAccepted(false);
                setNewVersionNotice(true);
                await refetch();
            } else {
                setError(t('membership.codeOfConduct.acceptError'));
            }
        }
    };

    const handleRetryLoad = async () => {
        await refetch();
        // If the org no longer has a pending version, refresh app-data so the gate can clear.
        await invalidateAppData();
    };

    const renderBody = () => {
        if (isLoading || (isRefetching && !version)) {
            return (
                <View style={styles.centered}>
                    <ActivityIndicator color={theme.colors.primary}/>
                </View>
            );
        }

        if (isError || !version) {
            return (
                <View style={styles.centered}>
                    <Text style={styles.errorText}>{t('membership.codeOfConduct.loadError')}</Text>
                    <TouchableOpacity style={styles.secondaryButton} onPress={handleRetryLoad}>
                        <Text style={styles.secondaryButtonText}>{t('membership.codeOfConduct.retry')}</Text>
                    </TouchableOpacity>
                </View>
            );
        }

        return (
            <>
                <ScrollView style={styles.scroll} contentContainerStyle={styles.scrollContent}>
                    <Markdown style={markdownStyles(theme)}>{version.content}</Markdown>
                </ScrollView>

                <View style={styles.footer}>
                    {newVersionNotice && (
                        <Text style={styles.noticeText}>{t('membership.codeOfConduct.newVersion')}</Text>
                    )}

                    <TouchableOpacity
                        style={styles.checkboxRow}
                        onPress={() => setAccepted(value => !value)}
                        disabled={busy}
                        activeOpacity={0.7}
                        accessibilityRole="checkbox"
                        accessibilityState={{checked: accepted, disabled: busy}}
                    >
                        {accepted
                            ? <SquareCheck size={22} color={theme.colors.primary}/>
                            : <Square size={22} color={theme.colors.textSecondary}/>}
                        <Text style={styles.checkboxLabel}>
                            {t('membership.codeOfConduct.acceptLabel', {title: version.title})}
                        </Text>
                    </TouchableOpacity>

                    {error && <Text style={styles.errorText}>{error}</Text>}

                    <TouchableOpacity
                        style={[styles.acceptButton, (!accepted || busy) && styles.acceptButtonDisabled]}
                        onPress={handleAccept}
                        disabled={!accepted || busy}
                        activeOpacity={0.7}
                    >
                        {busy
                            ? <ActivityIndicator color={theme.colors.background}/>
                            : <Text style={styles.acceptButtonText}>{t('membership.codeOfConduct.accept')}</Text>}
                    </TouchableOpacity>
                </View>
            </>
        );
    };

    return (
        <Modal
            visible={true}
            transparent={true}
            animationType="slide"
            onRequestClose={() => undefined}
        >
            <View style={styles.modalOverlay}>
                <View style={styles.modalContent}>
                    <View style={styles.modalHeader}>
                        <Text style={styles.modalTitle}>
                            {version ? version.title : t('membership.codeOfConduct.heading')}
                        </Text>
                        <Text style={styles.modalSubtitle}>{t('membership.codeOfConduct.subtitle')}</Text>
                    </View>
                    {renderBody()}
                </View>
            </View>
        </Modal>
    );
};

const markdownStyles = (theme: ThemeType) => ({
    body: {
        color: theme.colors.textSecondary,
        fontSize: 14,
        lineHeight: 20,
    },
    heading1: {
        color: theme.colors.text,
        fontSize: 18,
        fontWeight: '700' as const,
        marginBottom: 8,
        marginTop: 16,
    },
    heading2: {
        color: theme.colors.text,
        fontSize: 16,
        fontWeight: '600' as const,
        marginBottom: 6,
        marginTop: 12,
    },
    heading3: {
        color: theme.colors.text,
        fontSize: 14,
        fontWeight: '600' as const,
        marginBottom: 4,
        marginTop: 8,
    },
    paragraph: {
        color: theme.colors.textSecondary,
        fontSize: 14,
        lineHeight: 20,
        marginBottom: 8,
    },
    strong: {
        color: theme.colors.text,
        fontWeight: '600' as const,
    },
    em: {
        color: theme.colors.text,
        fontStyle: 'italic' as const,
    },
    link: {
        color: theme.colors.primary,
        textDecorationLine: 'underline' as const,
    },
    list_item: {
        color: theme.colors.textSecondary,
        fontSize: 14,
        lineHeight: 20,
        marginBottom: 4,
    },
    bullet_list: {
        marginBottom: 8,
    },
    ordered_list: {
        marginBottom: 8,
    },
    blockquote: {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderLeftWidth: 3,
        borderLeftColor: theme.colors.primary,
        paddingLeft: 12,
        paddingVertical: 8,
        marginVertical: 8,
    },
    hr: {
        backgroundColor: theme.colors.border,
        height: 1,
        marginVertical: 16,
    },
});

const createStyles = (theme: ThemeType, insets: {top: number, bottom: number}) => StyleSheet.create({
    modalOverlay: {
        flex: 1,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        justifyContent: 'flex-end',
        paddingTop: insets.top + 24,
    },
    modalContent: {
        backgroundColor: theme.colors.background,
        borderTopLeftRadius: 24,
        borderTopRightRadius: 24,
        paddingTop: 12,
        paddingBottom: insets.bottom + 20,
        maxHeight: '100%',
        flexShrink: 1,
    },
    modalHeader: {
        paddingHorizontal: 24,
        paddingVertical: 20,
        borderBottomWidth: 1,
        borderBottomColor: theme.colors.border,
    },
    modalTitle: {
        fontSize: 22,
        fontWeight: 'bold',
        color: theme.colors.text,
        marginBottom: 4,
    },
    modalSubtitle: {
        fontSize: 14,
        color: theme.colors.textSecondary,
    },
    centered: {
        paddingHorizontal: 24,
        paddingVertical: 32,
        alignItems: 'center',
    },
    scroll: {
        flexGrow: 0,
        flexShrink: 1,
    },
    scrollContent: {
        paddingHorizontal: 24,
        paddingVertical: 12,
    },
    footer: {
        paddingHorizontal: 20,
        paddingTop: 12,
        borderTopWidth: 1,
        borderTopColor: theme.colors.border,
    },
    noticeText: {
        fontSize: 13,
        color: theme.colors.primary,
        fontWeight: '600',
        marginBottom: 12,
        textAlign: 'center',
    },
    checkboxRow: {
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: 8,
        marginBottom: 8,
    },
    checkboxLabel: {
        flex: 1,
        marginLeft: 12,
        fontSize: 15,
        color: theme.colors.text,
        lineHeight: 20,
    },
    acceptButton: {
        backgroundColor: theme.colors.primary,
        borderRadius: 12,
        padding: 18,
        alignItems: 'center',
    },
    acceptButtonDisabled: {
        opacity: 0.5,
    },
    acceptButtonText: {
        fontSize: 16,
        fontWeight: '600',
        color: theme.colors.background,
    },
    secondaryButton: {
        marginTop: 16,
        paddingVertical: 12,
        paddingHorizontal: 24,
    },
    secondaryButtonText: {
        fontSize: 16,
        color: theme.colors.textSecondary,
        fontWeight: '500',
    },
    errorText: {
        fontSize: 13,
        color: '#DC2626',
        textAlign: 'center',
        paddingHorizontal: 4,
        paddingBottom: 8,
    },
});
